import styled from '@emotion/styled';

import { TwoColumnWrapper } from '@/components/blocks/TwoColumnLayout/TwoColumnWrapper';
import { screen } from '@/components/common/breakpoints';
import { PrimaryButton } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Image } from '@/components/common/image';
import { Tag } from '@/components/common/Tag/Tag';
import { ConditionalAnimateWrapper } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { getTextColour } from '@/lib/colourUtils';
import { useMediaQuery } from '@/lib/useMediaQuery';

import type { SanityProductHighlightType } from '@/types/sanity';

export interface ProductHighlightProps extends SanityProductHighlightType {}

const ProductHighlight = ({
  caption,
  heading,
  subheading,
  action,
  animate,
  tag,
  image,
  columnsSplit = 'even',
  isCaptionUppercase = true,
  backgroundColour,
}: ProductHighlightProps) => {
  const hasDefaultImage = image?.asset?._ref === undefined;

  const isDesktopOrLarger = useMediaQuery(screen.mdQuery);
  const isTabletOrLarger = useMediaQuery(screen.smQuery);
  const isExtraLarge = useMediaQuery(screen.xlQuery);

  const isMobile = !isTabletOrLarger;
  const isTablet = isTabletOrLarger && !isDesktopOrLarger;

  let imagePath = '/images/ProductHighlight/product-highlight-xl.svg';

  if (isTablet) {
    imagePath = '/images/ProductHighlight/product-highlight-tablet.svg';
  } else if (isMobile) {
    imagePath = '/images/ProductHighlight/product-highlight-mobile.svg';
  } else if (isDesktopOrLarger && !isExtraLarge) {
    imagePath = '/images/ProductHighlight/product-highlight-lg.svg';
  }

  const backgroundColourToken =
    backgroundColour?.token || '--background-subtle';

  return (
    <Section
      verticalPadding
      backgroundColour={backgroundColourToken}
      colour={getTextColour(backgroundColourToken)}
    >
      <ConditionalAnimateWrapper animate={animate}>
        <TwoColumnWrapper columnsSplit={columnsSplit}>
          {hasDefaultImage ? (
            <DefaultImageContainer>
              <img src={imagePath} alt="Snapshot of our product" />
            </DefaultImageContainer>
          ) : (
            <ImageContainer columnsSplit={columnsSplit}>
              <Image
                asset={image.asset}
                alt={image?.alternateText}
                isDecorativeImage={image?.isDecorativeImage}
              />
            </ImageContainer>
          )}
          <CopyContainer hasDefaultImage={hasDefaultImage}>
            {tag?.tagText && (
              <Tag
                bgColour={tag?.tagColour}
                margin="0 0 var(--spacing-xxx-small) 0"
              >
                {tag.tagText}
              </Tag>
            )}
            <CaptionHeadingDescription
              desktopAlignment={
                columnsSplit === 'singleColumn' ||
                columnsSplit === 'singleColumnReverse'
                  ? 'center'
                  : 'start'
              }
              mobileAlignment="center"
              captionHeadingDescription={{
                caption,
                heading,
                description: subheading,
              }}
              padding={{ description: 200 }}
              isCaptionUppercase={isCaptionUppercase}
            />
            {action?.buttonLabel && (
              <PrimaryButton
                iconPosition="right"
                href={action?.buttonLink}
                aria-label={action.buttonAccessibleLabel}
                componentTheme="light"
                className={
                  action.linkTrackingTag &&
                  `tracking-${action.linkTrackingTag.blockName}__${action.linkTrackingTag.blockAction}`
                }
              >
                {action.buttonLabel}
              </PrimaryButton>
            )}
          </CopyContainer>
        </TwoColumnWrapper>
      </ConditionalAnimateWrapper>
    </Section>
  );
};

const DefaultImageContainer = styled.div`
  position: relative;
  min-height: 264px;

  &::after {
    content: '';
    position: absolute;
    background-color: var(--colour-border-subtle);
    z-index: 3;
    bottom: 0;
    right: calc(0px - var(--section-gutter));
    height: 2px;
    width: calc(100% + var(--section-gutter));
  }

  > img {
    position: absolute;
  }

  ${screen.sm} {
    min-width: none;
    min-height: 268px;
    margin-top: var(--spacing-500);

    > img {
      left: 0;
      right: 0;
      top: -22%;
      margin: auto;
      z-index: 0;
      max-height: 550px;
      max-width: 704px;
      width: 100%;
    }
  }
  ${screen.sm} {
    &::after {
      right: 0;
      left: 0;
      margin: auto;
      width: 100%;
    }
  }

  ${screen.md} {
    min-width: 477px;
    min-height: 486px;
    margin-top: 0px;

    &::after {
      display: none;
    }

    > img {
      left: calc(-95px - var(--section-gutter));
      right: auto;
      top: -7.5%;
      max-height: none;
      max-width: none;
      width: auto;
    }
  }

  ${screen.lg} {
    min-width: 522px;
    min-height: 486px;

    > img {
      left: calc(-40px - var(--section-gutter));
    }
  }

  ${screen.xl} {
    min-width: 690px;
    min-height: 486px;
    > img {
      left: -5%;
    }
  }
`;

const ImageContainer = styled.div<{ columnsSplit: string }>`
  display: flex;
  justify-content: center;

  > img {
    max-height: 560px;
    max-width: 660px;
  }

  ${screen.md} {
    > img {
      max-height: 900px;
      max-width: 900px;
    }
  }

  ${({ columnsSplit }) =>
    columnsSplit === 'imageLeft' &&
    `
    position: relative;
    border-bottom: 2px solid var(--colour-border-subtle);
    overflow: hidden;
    > img {
      margin-bottom: -2.6rem;
    }
    ${screen.sm} {
      border: none;
      overflow: visible;
      > img {
      margin-bottom: 0;
    }
    }
  `}
`;

const CopyContainer = styled.div<{ hasDefaultImage: boolean }>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  text-align: center;
  z-index: 2;
  position: relative;
  padding-top: var(--spacing-400);

  ${({ hasDefaultImage }) =>
    hasDefaultImage &&
    `
    &::before {
      content: '';
      position: absolute;
      transform: translateX(-50%);
      left: 50%;
      top: 0;
      width: 100vw;
      height: 110%;
      background-color: var(--colour-white);
      z-index: -1;
    }
    ${screen.md} {
    &::before {
      content: none;
      display: none;
      }
    }
  `}

  ${screen.md} {
    &::before {
      content: none;
    }

    padding-top: 0;
    align-items: flex-start;
    text-align: left;
    margin-top: auto;
    margin-bottom: auto;
  }
`;

export { ProductHighlight };
